import * as React        from "react";
import {cx}              from "emotion";
import {faCoins}         from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import "./ai-text-from-image-modal.scss";

import {XdSentry}        from "@Common/libs/sentry/xd-sentry";
import {cs}              from "@Common/react/chain-services";
import {consumeContext}  from "@Common/react/context";
import {Load2}           from "@Common/react/load2";
import {UseRef}          from "@Common/react/use-ref";
import {UseState}        from "@Common/react/use-state";
import {ModalService3}   from "@Common/ui-components/modal-service-3/modal-service-3";
import {ScreenLoader}    from "@Common/ui-components/loading-spinners/screen-loader";
import {ButtonSpinner}   from "@Common/ui-components/loading-spinners/button-spinner";
import {UploadButton}    from "@Common/ui-components/upload-button/upload-button";
import {ErrorMessage}    from "@Common/ui-components/photo-dashboard/components/error-message";
import {AiToolInteractionTrigger} from "./ai-tool-interaction-trigger";

import {OdometerAnimation} from "./odometer-animation";


export const AiTextFromImageModal = ({
    idOrder,
    maxCharOverride = false,
    maxChars,
    setFieldVal,
    next: rootNext
}) => cs(
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("userLevels"),
    consumeContext("session"),
    ["idCompany", ({company}, next) => next(company.idCompany)],
    ["idBranch", ({session}, next) => next(session.data.agent.idBranch)],
    ["idAgent", ({session}, next) => next(session.data.agent.idAgent)],
    ["subscriptions", ({apis, themeScheme, subscriptionModal, idAgent}, next) => Load2({
           _key: idAgent,
           fetch: () =>  apis.next.subscription.getDetails({idAgent}),
           next,
       })
    ],
    ["companyEligible", ({company}, next) => next(company?.companyIndustryName === "RRE")],
    ["textGenerating", ({}, next) => UseState({initValue: false, next})],
    ["generatedText", ({}, next) => UseState({initValue: null, next})],
    ["selectedImage", ({}, next) => UseState({initValue: false, next})],
    ["errorMessage", ({}, next) => UseState({initValue: "", next})],
    // TODO: CREATE A NEW COMPONENT TO HOUSE CARD COMPONENT
    ["createImageCards", ({selectedImage, errorMessage}, next) => next((image) => {
        return (
            <div
                className={selectedImage.value === image.id ? "selected-image image-card" : "image-card"}
                key={image.id}
                id={image.id}
                onClick={async () => {
                    errorMessage.onChange("");
                    selectedImage.onChange(selectedImage.value === image.id ? false : image.id)
            }}
            >
                <img src={image.thumbnailUrl} alt="order image"/>
            </div>
        );
    })],
    ["modal", ({ generatedText, selectedImage, errorMessage}, next) => ModalService3({
        next: ({close}) => next({close}),
        onClose: async () => {
            generatedText.onChange(null);
            selectedImage.onChange(false);
            errorMessage.onChange("");
        },
        xToClose: true,
        ignorePassiveClose: true,
        rootNext,
        contentClassName: "ai-text-from-image-j73",
        width: 644,
        xdQaName: "imageEditModal",
    })],
    ["usersRemainingAiActions", ({apis, subscriptions}, next) => subscriptions.value && Load2({
        _key: "images_" + idOrder,
        fetch: () => apis.next.gpt.getUserAiImageToolUses({ "idSubscription": subscriptions.value?.data?.find(item => item.subscribed == true).idPrimary }),
        captureException: true,
        next,
    })],
    ["generateTextFromImage", ({apis, idCompany, idBranch, idAgent, subscriptions, textGenerating, generatedText, usersRemainingAiActions}, next) => next(async (imageUrl) => {
        textGenerating.onChange(true);
        let responseFromOpenAi = await apis.next.gpt.aiTextFromImage({
            cueImageUrl: imageUrl,
            maxChars,
            maxCharOverride,
            idOrder,
            idCompany: idCompany,
            idBranch: idBranch,
            idAgent: idAgent,
        });
        AiToolInteractionTrigger();
        textGenerating.onChange(false);
        if (responseFromOpenAi.error) {
            generatedText.onChange("Error generating text from image, please try again.")
            return;
        }
        generatedText.onChange(responseFromOpenAi['choices'][0]['message']['content']);
        await apis.next.gpt.updateRemainingAiActions({ "idSubscription": subscriptions.value?.data?.[0]['idPrimary'] });
        usersRemainingAiActions.reload();
    })],
    ["imageCollectionRef", ({}, next) => UseRef({next})],
    ["uploadButtonRef", ({}, next) => UseRef({next})],
    ["uploadProgressPercent", ({}, next) => UseState({initValue: 0, next})],
    ["showFooterUpload", ({}, next) => UseState({initValue: false, next})],
    ["orderImages", ({apis}, next) => Load2({
        _key: "images_" + idOrder,
        fetch: () => apis.next.gallery.orders.getImages(idOrder),
        captureException: true,
        next,
    })],
    ["scrollToSelectedImage", ({imageCollectionRef}, next) => next((newSelectedImageId) => {
        if (!newSelectedImageId) return;
        const selectedImageElement = imageCollectionRef.get().querySelector(`.image-card[id="${newSelectedImageId}"]`);
        if (selectedImageElement) {
            selectedImageElement.scrollIntoView({behavior: "smooth", block: "center"});
        }
    })],
    ["onUpload", ({apis, company, errorMessage, uploadProgressPercent, orderImages, selectedImage, scrollToSelectedImage}, next) => next(async (event) => {

        // clear the error message if one is set
        if (errorMessage.value) errorMessage.onChange("");

        // issue error if problem capturing the file
        if (!event.target || !event.target.files.length || Array.from(event.target.files).find(x => !x.size)) {
            errorMessage.onChange("There was an error applying your image selection. Please try again.");
            return;
        }

        // validate all files
        const allowedTypes = ["image/jpeg", "image/png", "image/svg+xml", "application/pdf"];
        for (const file of event.target.files) {
            if (!allowedTypes.includes(file.type)) {
                errorMessage.onChange("Your image is not supported. Please select a .jpg, .png, .svg or .pdf file.");
                return;
            }
        }

        // find total payload to be uploaded for calculating progress percent
        let totalPayload = Array.from(event.target.files).reduce((total, file) => total + file.size, 0);
        let uploadedPayload = 0;
        let uploadedFile;

        // initialize variables used in process for warning of images skipped due to low quality settings
        const numFiles = event.target.files.length; // needed because event.target won't exist when the final error message is constructed

        // upload all files
        for (const file of event.target.files) {
            // upload the file
            try {
                uploadedFile = await apis.next.gallery.orders.upload({
                    orderId: idOrder,
                    formData: {file},
                    onUploadProgress: (percent) => {
                        uploadProgressPercent.onChange( Math.ceil((uploadedPayload + (file.size * percent / 100)) / totalPayload * 100) );
                    },
                });
                selectedImage.onChange(uploadedFile.id);
            } catch (e) {}

            // issue an error if no response
            if (!uploadedFile) {
                XdSentry.addBreadcrumb({
                    category: "aiVision",
                    message: "User attempted to upload an image that failed during transmission.",
                    level: XdSentry.Severity.Error,
                    data: {
                        idOrder,
                        uploadFileData: {file},
                    }
                });
                XdSentry.captureMessage("Image Upload Error", XdSentry.Severity.Log);
                errorMessage.onChange("There was an error uploading your file.");
                return;
            }

            // issue error if no path was returned
            if (!uploadedFile.printUrl) {
                XdSentry.addBreadcrumb({
                    category: "aiVision",
                    message: "User uploaded an image that that did not return a print URL.",
                    level: XdSentry.Severity.Error,
                    data: {
                        uploadFileData: file,
                        returnedData: uploadedFile,
                    }
                });
                XdSentry.captureMessage("Image Upload Error", XdSentry.Severity.Log);
                errorMessage.onChange("There was an error uploading your file.");
                return;
            }

            // add size to payload for calculating upload percent
            uploadedPayload += file.size;
        }

        uploadProgressPercent.onChange(0);
        await orderImages.reload();
        scrollToSelectedImage(uploadedFile.id);
    })],
    (({orderImages}, next) => next( console.log("orderImages: ", orderImages) )),

    ({modal, createImageCards, textGenerating, generatedText, usersRemainingAiActions, selectedImage, generateTextFromImage, imageCollectionRef, uploadButtonRef, orderImages, errorMessage, uploadProgressPercent, showFooterUpload, onUpload}) => (
        <>
            <div className="input-container">
                <div className="header">
                    {orderImages.value?.length ? "Choose an image to generate text from" : "upload an image to generate text from"}
                </div>
                <hr/>
                <div>
                    <div className="error-box">
                        {usersRemainingAiActions.value == 0 ?
                            <ErrorMessage text="You have no more remaining usages for this tool." isNotice={true}/> :
                                <ErrorMessage text={errorMessage.value}/>
                        }
                    </div>
                        {(orderImages.loading === true)
                            ?
                                <div className="loading-spinner-container">
                                    <ScreenLoader />
                                </div>
                            :
                                // TODO: MAKE A UI ELEMENT TO SHOW WHEN PHOTOS ARE HIDDEN BY OVERFLOW & SCROLLABLE
                                <>
                                    <div className="image-collection"
                                        ref={ref => imageCollectionRef.set(ref)}
                                        onScroll={(e) => {

                                            if (!uploadButtonRef.get()) return;
                                            const topPosition = imageCollectionRef.get().scrollTop;
                                            const uploadButtonPosition = uploadButtonRef.get().offsetTop + uploadButtonRef.get().offsetHeight;

                                            if (!showFooterUpload.value && topPosition > uploadButtonPosition) {
                                                showFooterUpload.onChange(true);
                                            }
                                            else if (showFooterUpload.value && topPosition <= uploadButtonPosition) {
                                                showFooterUpload.onChange(false);
                                            }

                                        }}
                                    >
                                        <div className="image-card">
                                            <h3>Upload a Photo</h3>
                                            <p className="browse">
                                                <UploadButton
                                                    buttonRef={uploadButtonRef}
                                                    onUpload={onUpload}
                                                    uploadProgressPercent={uploadProgressPercent.value}
                                                    allowMultipleFiles={false}
                                                />
                                            </p>
                                            <p>
                                                Images must be JPEG (.jpg, .jpeg, .jpe) PNG, PDF or SVG format. Less than 10MB in file size.
                                            </p>
                                        </div>
                                        {orderImages.value?.map(createImageCards)}
                                    </div>
                                    <div className={cx("footer upload", {hidden: !showFooterUpload.value})}>
                                        <UploadButton
                                            buttonText="Upload a Photo"
                                            onUpload={onUpload}
                                            uploadProgressPercent={uploadProgressPercent.value}
                                            allowMultipleFiles={false}
                                        />
                                        <p>Images must be JPEG (.jpg, .jpeg, .jpe) PNG or PDF format. Less than 10MB in file size.</p>
                                    </div>
                                </>

                        }
                </div>
                <hr className="no-margin-hr" />
                <div className="generate-text-button-container">
                    <div
                        className={
                            orderImages.loading === true || usersRemainingAiActions.value == 0 ?
                                "generate-text-button generate-text-button-inactive" :
                                    "generate-text-button"
                        }
                        onClick={async () => {
                            errorMessage.onChange("");
                            if (usersRemainingAiActions.value == 0) {
                                return;
                            }
                            if (!selectedImage.value) {
                                errorMessage.onChange("Please select an image to generate text from.");
                                return;
                            }
                            await generateTextFromImage(orderImages.value.find(image => image.id === selectedImage.value).thumbnailUrl);
                            selectedImage.onChange(false);
                        }}
                    >
                            <div className="button-icon">
                                {orderImages.loading === true ? <ButtonSpinner spinnerColor="white"/> : <img src='/next_images/ai-tools/ai-sparkles.svg' width="20px"/>}
                            </div>
                            <div className="button-text">
                                GENERATE TEXT
                            </div>
                    </div>
                    <div
                        className={
                            orderImages.loading === true || usersRemainingAiActions.value == 0 ?
                                "remaining-ai-actions-container remaining-ai-actions-container-inactive" :
                                    "remaining-ai-actions-container"
                        }
                    >
                        <FontAwesomeIcon icon={faCoins} size="lg" className="ai-actions-icon"/>
                        {!usersRemainingAiActions.loading && <OdometerAnimation value={usersRemainingAiActions.value} size="18px" />}
                    </div>
                </div>
                {textGenerating.value
                    ?
                        <div className="generated-text-loading">
                            <ScreenLoader loaderColor="white"/>
                        </div>
                    :
                        null
                }
                {generatedText.value?.length
                    ?
                        <div className="output-container">
                            <div className="output-header" xd-qa-type="label" xd-qa-name="Output">
                                Output
                            </div>
                            {/* TODO: MAKE FAILURE MESSAGE DISPLAY WITH RE-TRY FUNCTIONALITY*/}
                            <div className={ generatedText.value?.startsWith("Error generating") ?
                            "generated-text-box text-generation-error" : "generated-text-box"}>
                                {generatedText.value}
                            </div>
                        </div>
                    :
                        null
                }
                    {(generatedText.value?.length || textGenerating.value)
                        ?
                            <div className="insert-button-container">
                                <div className={( generatedText.value?.length && !generatedText.value?.startsWith("Error generating") ) ? "insert-button" : "insert-button insert-button-inactive"}
                                    xd-qa-type="button" xd-qa-name="Insert button"
                                    onClick={() => {
                                        if (generatedText.value?.length && !generatedText.value?.startsWith("Error generating")) {
                                            setFieldVal(generatedText.value);
                                            generatedText.onChange(null);
                                            selectedImage.onChange(false);
                                            modal.close();
                                        }
                                    }}
                                >
                                    Insert
                                </div>
                            </div>
                        :
                            null
                    }
            </div>
        </>
    )
);